import { newSettingDataSetType } from "../../../types/types";
import SignallampContentConfig from "../scripts/signallamp_content_config";
import { translate } from "@/main";

/**
 * パトランプ設定情報入力値チェック処理。
 * @param {newSettingDataSetType} newSettingDataSet チェック対象データ
 * @param addDeviceId
 * @param addMails
 * @param newkeyColors
 * @param alertCheck
 * @param mailSendInterval
 * @param addDbTable
 * @param addLocation
 * @param userID
 * @param authStoreName
 * @returns {string | boolean} チェック結果
 */
export function checkUpdateSignallampSettingData(
  newSettingDataSet: newSettingDataSetType
): string | boolean {
  // 送信者チェック
  const checkUserIdResult = checkUserId(newSettingDataSet.userID);
  // 認証情報ストア名
  const checkAuthStoreNameResult = checkAuthStoreName(newSettingDataSet.authStoreName);
  // テーブル名入力チェック
  const checkTableNameResult = checkTableName(newSettingDataSet.addDbTable);
  // 閾値有効入力チェック
  const checkAlertFlagResult = checkAlertFlag(newSettingDataSet.alertCheck);
  const checkMatchColorAndFlagResult = checkMatchColorAndFlag(newSettingDataSet.alertCheck, newSettingDataSet.newkeyColors);
  // 送信先入力チェック
  const checkMailsResult = newSettingDataSet.addMails === "--" ? { result: true, msg: "" } : checkMails(
    newSettingDataSet.addMails,
    checkAlertFlagResult
  );
  // 送信間隔入力チェック
  const checkMailSendIntervalResult = checkMailSendInterval(newSettingDataSet.mailSendInterval);
  // 設置場所入力チェック
  const checkLocationNameResult = newSettingDataSet.addLocation !== "" ? checkLocationName(newSettingDataSet.addLocation) : { result: true };
  // デバイスID入力チェック
  const checkDeviceIdResult = newSettingDataSet.addDeviceId !== "" ? checkDeviceId(newSettingDataSet.addDeviceId) : { result: false, msg: `${translate("message.deviceIdCannotBeEmpty")}` };

  if (!checkUserIdResult.result) {
    return checkUserIdResult.msg;
  }
  if (!checkDeviceIdResult.result) {
    return checkDeviceIdResult.msg;
  }
  if (!checkAuthStoreNameResult.result) {
    return checkAuthStoreNameResult.msg;
  }
  if (!checkTableNameResult.result) {
    return checkTableNameResult.msg;
  }
  if (!checkAlertFlagResult.result) {
    return checkAlertFlagResult.msg;
  }
  if (!checkMatchColorAndFlagResult.result) {
    return checkMatchColorAndFlagResult.msg;
  }
  if (!checkMailsResult.result) {
    return checkMailsResult.msg;
  }
  if (!checkMailSendIntervalResult.result) {
    return checkMailSendIntervalResult.msg;
  }
  if (!checkLocationNameResult.result !== null) {
    return checkLocationNameResult.msg;
  }
}

function checkUserId(userID: string) {
  const cognitoUserId = JSON.parse(localStorage.getItem("iotKyotoAuth")).userName;
  if (userID !== cognitoUserId) {
    return { result: false, msg: `${translate("message.userIdIsInvalid")}` };
  }
  return { result: true };
}

function checkAuthStoreName(authStoreName: any) {
  const regex = /^[\w\W-_亜-熙ぁ-んァ-ヶ]*$/g;
  const checkAuthStoreNameResult = authStoreName.match(regex) ? { result: true } : { result: false, msg: `${translate("message.credentialStoreNameCannotHaveSymbolsExceptHyphensAndUnderscores")}` };
  return checkAuthStoreNameResult;
}

function checkTableName(tableName: any) {
  const regex = /^[\w-_\.]*$/g;
  const count = /^[\w-_\.]{3,255}$/g;
  if (!tableName.match(regex)) {
    return { result: false, msg: `${translate("message.tableNameCannotHaveSymbolsExceptHyphensAndUnderscores")}` };
  }
  if (!tableName.match(count)) {
    return { result: false, msg: `${translate("message.tableNameMustHave3To255Characters")}` };
  }
  return { result: true };
}

function checkAlertFlag(flags: boolean[]) {
  if (!flags) {
    return { result: true };
  }
  for (const key in flags) {
    if (typeof flags[key] !== "boolean") {
      return { result: false, msg: `${translate("message.formatOfAlertSwitchIsInvalid")}` };
    }
  }
  return { result: true };
}

function checkMatchColorAndFlag(flags: boolean[], keyColors: string[]) {
  for (const key in flags) {
    if (flags[key] === true && (!keyColors[key] || keyColors[key] === "undefined")) {
      return { result: false, msg: `${translate("message.makeSureLightColorIsSetForAlertTargetStack")}` };
    }
  }
  return { result: true };
}

function checkMails(mails: string, checkAlertFlagResult: any | null) {
  if (!mails && !checkAlertFlagResult) {
    return { result: true };
  }
  let newMailsList: string[] = [];
  if (!mails) {
    return { result: true };
  } else if (checkAlertFlagResult.result) {
    newMailsList = mails.split(",");
  } else {
    return { result: false, msg: `${translate("message.youEnteredAlertEmailAddressButNotAlertTarget")}` };
  }
  const results: boolean[] = newMailsList.map((mail: string) => {
    return (
      mail.match(
        /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i,
      ) === null
    );
  });
  if (newMailsList.length === 1 && newMailsList[0] === "") {
    return { result: true };
  } else if (results.indexOf(true) >= 0) {
    return { result: false, msg: `${translate("message.invalidEmail")}` };
  } else if (newMailsList.length >= 5) {
    return { result: false, msg: `${translate("message.youCanRegisterUpTo5EmailAddresses")}` };
  } else {
    return { result: true };
  }
}

function checkMailSendInterval(mailSendInterval: number) {
  const sendDurationList = [];
  for (const key in SignallampContentConfig.sendDurationList()) {
    sendDurationList.push(SignallampContentConfig.sendDurationList()[key].value);
  }
  if (sendDurationList.indexOf(mailSendInterval) < 0) {
    return { result: false, msg: `${translate("message.makeSureMailAlertIntervalIsLongerThan0Sec")}` };
  }
  return { result: true };
}

function checkLocationName(location: any) {
  const regex = /^[\w\W-_亜-熙ぁ-んァ-ヶ]*$/g;
  const count = /^[\w\W-_亜-熙ぁ-んァ-ヶ]{0,20}$/g;
  if (!location.match(regex)) {
    return { result: false, msg: `${translate("message.locationNameCannotHaveSymbolsExceptHyphensAndUnderscores")}` };
  }
  if (!location.match(count)) {
    return { result: false, msg: `${translate("message.locationNameCanHaveMaximumOf20Chars")}` };
  }
  return { result: true };
}

function checkDeviceId(deviceId: any) {
  const regex = /^[\w-:\.]*$/g;
  const count = /^[\w-:\.]{1,15}$/g;
  for (const key in deviceId) {
    if (!deviceId[key].match(regex)) {
      return { result: false, msg: `${translate("message.deviceIdCannotHaveSymbolsExceptHyphensUnderscoresAndColons")}` };
    }
    if (!deviceId[key].match(count)) {
      return { result: false, msg: `${translate("message.deviceIdCanHaveMaximumOf15Chars")}` };
    }
  }
  return { result: true };
}
